import React, { useState, useEffect, useMemo } from "react";
import "./calculateTask3LG.less";

type DataMode = "massFlow" | "molarFlow" | "massConcentration" | "moleFrac";

interface Props {
    streams: any[];
    input: any;
    isHide: boolean;
}

const ResultComponentTable: React.FC<Props> = ({ streams, input, isHide }) => {
    const [mode, setMode] = useState<DataMode>("moleFrac");
    const [materials, setMaterials] = useState<any[]>([]);

    const modes = [
        { value: "moleFrac", label: "摩尔分率" },
        { value: "massFlow", label: "质量流量（kg/h）" },
        { value: "molarFlow", label: "摩尔流量（mol/h）" },
        { value: "massConcentration", label: "质量浓度（mg/m³）" },
    ];

    useEffect(() => {
        if (input?.inputs?.streams) {
            const m = input.inputs.streams.find((item: any) => item.materials?.length);
            setMaterials(m?.materials ?? []);
        }
    }, [input]);

    // 摩尔分率保留 4 位小数更美观
    const fmt = (v: any, fixed = 2) =>
        v == null || isNaN(Number(v)) ? "—" : Number(v).toFixed(fixed);

    // 计算当前模式下的值
    const getVal = (stream: any, design: any, idx: number) => {
        if (mode === "massFlow") {
            const calc = (stream.f_kg_per_h ?? 0) * (stream.mass_frac?.[idx] ?? 0);
            const des = (design.f_kg_per_h ?? 0) * (design.mass_frac?.[idx] ?? 0);
            return { des, calc };
        }
        if (mode === "molarFlow") {
            const calc = (stream.f_mol_per_h ?? 0) * (stream.molar_frac?.[idx] ?? 0);
            const des = (design.f_mol_per_h ?? 0) * (design.molar_frac?.[idx] ?? 0);
            return { des, calc };
        }
        if (mode === "massConcentration") {
            const calc = ((stream.f_kg_per_h ?? 0) * 1e6 * (stream.mass_frac?.[idx] ?? 0)) / (stream.f_nm3_per_h ?? 1);
            const des = ((design.f_kg_per_h ?? 0) * 1e6 * (design.mass_frac?.[idx] ?? 0)) / (design.f_nm3_per_h ?? 1);
            return { des, calc };
        }
        if (mode === "moleFrac") {
            const calc = stream.molar_frac?.[idx] ?? 0;
            const des = design.molar_frac?.[idx] ?? 0;
            return { des, calc, fixed: 4 };
        }
        return { des: 0, calc: 0 };
    };

    const totalColSpan = 1 + materials.length * 2;

    return (
        <div className="result-component-table-container">
            <div className="mode-selector-container">
                <span className="mode-selector-label">组分数据：</span>
                <div className="mode-buttons">
                    {modes.map((m) => (
                        <button
                            key={m.value}
                            className={`mode-button ${mode === m.value ? "active" : ""}`}
                            onClick={() => setMode(m.value as DataMode)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="table-container">
                <div className="table-scroll-wrapper">
                    <table className="component-data-table">
                        <thead>
                            {/* 第一层：组分名称 */}
                            <tr className="table-header">
                                <th rowSpan={2}>流股</th>
                                {materials.map((mat, i) => (
                                    <th key={i} colSpan={isHide ? 1 : 2}>{mat.name}</th>
                                ))}
                            </tr>
                            {/* 第二层：设计 / 计算 */}
                            {isHide ? null : (<>
                            <tr className="table-header">
                                {materials.map((_, i) => (
                                    <React.Fragment key={i}>
                                        <th>设计值</th>
                                        <th>计算值</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                            </>)}
                            
                        </thead>
                        <tbody>
                            {streams.length > 0 ? (
                                streams.map((stream, i) => {
                                    const d = stream.design ?? {};
                                    return (
                                        <tr key={i} className="table-row">
                                            <td>{stream.name}</td>
                                            
                                            {materials.map((_, idx) => {
                                                const { des, calc, fixed } = getVal(stream, d, idx);
                                                return (
                                                    <React.Fragment key={idx}>
                                                        {!isHide ? (<td>{fmt(des, fixed)}</td>
                                            ):null}           
                                                        <td>{fmt(calc, fixed)}</td>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={totalColSpan} className="empty-cell">请选择流股</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ResultComponentTable;